import { baseUrl } from "../../baseUrl";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { agentTableColumnDefs } from "./agentTableColumns";
import { PersonAddAlt1, Message } from "@mui/icons-material";
import { AddAgentModal } from "../../components/addAgentModal";
import { useCallback, useEffect, useRef, useState } from "react";
import { SendMessageModal } from "../../components/sendMessageModal";
import type { GridApi, ColumnState, GridReadyEvent } from "ag-grid-community";
import { FormSubmissionTable } from "../../components/common/formSubmissionTable";
import {
  formatPhoneNumberE164,
  isValidPhoneNumber
} from "../../utils/phoneValidator";
import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress
} from "@mui/material";

interface SortModel {
  colId: string;
  sort: "asc" | "desc";
}
interface AgentData {
  mortgages: Array<Record<string, unknown>>;
}

export function Agents() {
  const [filters, setFilters] = useState({});
  const [sortModel, setSortModel] = useState<SortModel[]>([]);
  const [page, _] = useState(1);
  const [pageSize, setPageSize] = useState(2000);
  const navigate = useNavigate();
  const [prevData, setPrevData] = useState<AgentData | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const gridColumnApiRef = useRef<GridApi>(null);

  const { data, isLoading, error, isSuccess } = useQuery({
    queryKey: ["mortgages", { filters, sortModel, page, pageSize }],
    queryFn: async () => {
      const hasFilters = Object.keys(filters).length > 0;
      const hasSorting = sortModel.length > 0;

      if (hasFilters || hasSorting) {
        const body: {
          page: number;
          limit: number;
          query: Record<string, unknown>;
        } = {
          page,
          limit: pageSize,
          query: {}
        };

        if (hasFilters) {
          const filterFields: Record<string, string> = {};
          for (const [field, filter] of Object.entries(
            filters as Record<string, { filter: string }>
          )) {
            const filterValue = filter.filter;

            if (field === "emailAddress") {
              setPageSize(1);
            }
            if (filterValue) {
              if (field === "phoneNumber") {
                try {
                  const formattedPhoneNumber = isValidPhoneNumber(
                    filterValue,
                    "CA"
                  )
                    ? formatPhoneNumberE164(filterValue, "CA")
                    : formatPhoneNumberE164(filterValue, "US")
                      ? formatPhoneNumberE164(filterValue, "US")
                      : filterValue;
                  filterFields[field] = formattedPhoneNumber;
                } catch {
                  filterFields[field] = "";
                }
              } else {
                filterFields[field] = filterValue;
              }
            }
          }
          body.query.filter = filterFields;
        }

        if (hasSorting) {
          const firstSort = sortModel[0] as {
            colId: string;
            sort: "asc" | "desc";
          };
          const sortField = firstSort.colId;
          const sortOrder = firstSort.sort === "asc" ? "ASC" : "DESC";

          body.query.sort = {
            order: sortOrder,
            field: sortField
          };
        }

        const response = await fetch(`${baseUrl}/mortgages/search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body)
        });
        setPageSize(500);
        return response.json();
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      });
      const response = await fetch(`${baseUrl}/mortgages?${params.toString()}`);

      return response.json();
    },
    retry: false
  });
  useEffect(() => {
    if (isSuccess) {
      setPrevData(data);
    }
  }, [isSuccess, data]);

  const onFilterChanged = useCallback(() => {
    const filterModel = gridApiRef.current?.getFilterModel();
    setFilters(filterModel || {});
  }, []);

  const onSortChanged = useCallback(() => {
    if (gridColumnApiRef.current) {
      const sortModel = gridColumnApiRef.current
        .getColumnState()
        .filter((col: ColumnState) => col.sort !== null)
        .map((col: ColumnState) => ({
          colId: col.colId,
          sort: col.sort as "asc" | "desc"
        }));
      setSortModel(sortModel);
    }
  }, []);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    gridColumnApiRef.current = params.api;
  }, []);

  const rowData = error ? prevData?.mortgages : data?.mortgages;
  const [openAddAgentDialog, setOpenAddAgentDialog] = useState(false);
  const [openSendMessageDialog, setOpenSendMessageDialog] = useState(false);

  if (!data && !isLoading) {
    return (
      <Container
        sx={{
          my: 5,
          maxWidth: {
            xs: "97%"
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center"
          }}
        >
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Data Found
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      sx={{
        mt: 3,
        maxWidth: {
          xs: "100%"
        }
      }}
      disableGutters={true}
    >
      {error && <Typography>Error loading data: {error.message}</Typography>}

      {isLoading && !prevData ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              maxWidth: {
                xs: "97%"
              },
              width: "100%"
            }}
          >
            <Typography variant="h4" gutterBottom>
              Agents
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                gap: 1,
                "@media (max-width: 600px)": {
                  gap: 0
                }
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => setOpenSendMessageDialog(true)}
                  type="button"
                  color="primary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginBottom: "10px",
                    "@media (max-width: 600px)": {
                      gap: 0,
                      justifyContent: "center"
                    }
                  }}
                >
                  <Message />
                  <Box
                    component="span"
                    sx={{
                      "@media (max-width: 600px)": {
                        display: "none"
                      }
                    }}
                  >
                    Send Message
                  </Box>
                </Button>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => setOpenAddAgentDialog(true)}
                  type="button"
                  color="primary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginBottom: "10px",
                    "@media (max-width: 600px)": {
                      gap: 0,
                      justifyContent: "center"
                    }
                  }}
                >
                  <PersonAddAlt1 />
                  <Box
                    component="span"
                    sx={{
                      "@media (max-width: 600px)": {
                        display: "none"
                      }
                    }}
                  >
                    Add Agent
                  </Box>
                </Button>
              </Box>
            </Box>
          </Box>
          <FormSubmissionTable
            columnDefs={agentTableColumnDefs}
            rowData={rowData || []}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            pageSize={pageSize}
            onGridReady={onGridReady}
            height="calc(100vh - 142px)"
            onCellClicked={(e) => navigate({ to: `/agents/${e.data.id}` })}
          />
        </Box>
      )}
      <AddAgentModal
        openDialog={openAddAgentDialog}
        setOpenDialog={setOpenAddAgentDialog}
      />
      <SendMessageModal
        openDialog={openSendMessageDialog}
        setOpenDialog={setOpenSendMessageDialog}
      />
    </Container>
  );
}
